<template>
  <div id="home">
    <lerner-header :pending="authPending"></lerner-header>
    <div class="flex">
      <div class="offset-lg-1 col-11 text-center">
        <h3 class="intro-headline my-4">Score big with sports fans and report writers!</h3>
      </div>
    </div>
    <div class="flex items-start lg:items-stretch mb-8">
      <div class="content offset-lg-1 col-lg-6 col-md-7" v-html="pageContent">
        <img class="devices" alt="Computer and tablet browsers with lernersports' homepage loaded" src="../../assets/images/LernerSports_computer_ipad.jpg"/>
        <p class="body-4">Lerner Sports is a database of high-interest biographies profiling sports
          superstars. Packed with fascinating facts, these bios explore the backgrounds, career-defining moments, and
          everyday lives of popular athletes. Lerner Sports is perfect for students in grades 2-5 developing research
          skills or looking for exciting sports content.</p>
      </div>
      <div class="offset-lg-1 col-lg-4 col-md-5 register-wrapper">
        <div class="register">
          <div class="flex flex-col items-center">
            <img class="register-logo" alt="logo" src="../../assets/images/2-lrn-spt-logo-252.svg"/>
            <div class="py-5 col-12 flex flex-col items-center">
              <h6 class="leading-none text-center">REGISTER</h6>
              <p class="body-12">FOR A</p>
              <p class="body-12 mb-3">FREE TRIAL!</p>
              <router-link class="btn btn-gray body-6 btn-md" to="/register">Free Trial</router-link>
            </div>
            <div class="divider">
              <hr/>
            </div>
            <div class="py-5 col-12 flex flex-col items-center">
              <p class="body-12">Or request</p>
              <p class="body-12 mb-3">pricing</p>
              <router-link class="btn btn-gray body-6 btn-md" to="/pricing">Request Pricing</router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
    <section class="callout key-features">
      <div class="flex items-center bg-lerner-blue">
        <div class="col-5">
          <h6>Key Features</h6>
        </div>
        <div class="col-7">
          <img src="../../assets/images/10-lrn-spt-feat-ban-570.svg" alt="key features stars"/>
        </div>
      </div>
      <div class="flex features" v-for="featureChunks in keyFeatures">
        <div class="col-lg-3 col-md-6 feature" v-for="feature in featureChunks">
          <h3 class="body-3 text-lerner-orange">{{ feature.title }}</h3>
          <p class="body-9">{{ feature.content }}</p>
        </div>
      </div>
      <div class="additional-features">
        <div class="flex">
          <div class="col-12">
            <h2 class="body-3 mt-6 mb-3">Additional Features</h2>
          </div>
        </div>
        <div class="flex flex-wrap">
          <div class="col-lg-3 col-md-6 additional-feature" v-for="featureChunks in additionalFeatures">
            <ul>
              <li class="body-9" v-for="feature in featureChunks" :key="feature.id">{{ feature.content }}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
    <section v-if="carouselSlides.length" class="callout news-reviews">
      <div class="flex items-center bg-lerner-blue">
        <div class="col-5">
          <h6>News & Reviews</h6>
        </div>
        <div class="col-7">
          <img src="../../assets/images/11-lrn-spt-news-ban-470.svg" alt="key features stars"/>
        </div>
      </div>
      <div class="relative overflow-hidden w-full">
        <div class="news-carousel">
          <carousel
              :loop="true"
              :items="2"
              :margin="8"
              :mouseDrag="false"
              :touchDrag="false"
              :dots="false"
          >
            <template slot="prev">
              <carousel-navigation ref="prevNav" :direction="'prev'"/>
            </template>
            <div class="news-reviews-slide" v-bind:style="slideStyle(slide)" v-for="slide in carouselSlides" :key="slide.id">
              <div v-html="slide.content"></div>
            </div>
            <template slot="next">
              <carousel-navigation :direction="'next'"/>
            </template>
          </carousel>
        </div>
      </div>
    </section>
    <div class="cta-wrapper">
      <div class="col-12">
        <div class="cta">
          <img class="hidden lg:block" alt="logo" src="../../assets/images/2-lrn-spt-logo-252.svg"/>
          <h6 class="leading-none text-center mb-0">Register</h6>
          <router-link class="btn btn-gray body-6 btn-md" to="/register">Free Trial</router-link>
          <router-link class="btn btn-gray body-6 btn-md" to="/pricing">Request Pricing</router-link>
        </div>
      </div>
    </div>
    <app-footer :pages="pages"></app-footer>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import carousel from 'vue-owl';

  import AppFooter from '@/component/AppFooter';
  import LernerHeader from '@/component/Header';
  import LoadingIndicator from '@/component/LoadingIndicator';
  import { CHANGE_DOC_TITLE } from '@/store/actions/app.actions';
  import { routeBaseTitle } from '@/router/utils';
  import CarouselNavigation from '@/component/CarouselNavigation';
  import FeatureService from '@/common/services/feature.service';

  export default {
    name: 'Home',

    components: {
      LernerHeader,
      LoadingIndicator,
      AppFooter,
      carousel,
      CarouselNavigation
    },

    data: function () {
      return {
        keyFeatures: [],
        additionalFeatures: []
      }
    },

    computed: {
      ...mapGetters([
        'pages',
        'authPending',
        'carouselSlides'
      ]),

      pageContent() {
        const homepage = this.pages.find(page => page.title.toLowerCase() === 'home');

        if (homepage) {
          return homepage.content;
        }

        return '';
      }
    },

    methods: {
      slideStyle: function (slide) {
        if (slide.featured_image) {
          return {
            backgroundImage: 'url(' + slide.featured_image + ')'
          }
        }

        return {
          background: '#484848'
        }
      }
    },

    created: function () {
      this.$store.dispatch(CHANGE_DOC_TITLE, {title: routeBaseTitle, useBaseTitle: false});

      FeatureService.getFeatures()
      .then(res => {
        this.additionalFeatures = res.additional_features;
        this.keyFeatures = res.key_features;
      });
    },
  };
</script>

<style lang="scss">
  #home {
    .intro-headline {
      @apply text-primary leading-tight;
      font-size: 44px;
    }

    .content {
      p {
        @apply leading-normal;
        font-family: config('fonts.primary');
        font-size: 18px;
      }

      img {
        @apply mb-6;
      }
    }

    h6 {
      color: #fdd073;
      font-size: 30px;
      text-transform: uppercase;
    }

    .register-wrapper {
      background-color: #2d4c90;
      border-top-left-radius: 25px;
      border-bottom-left-radius: 25px;
      @apply pl-6 py-5 my-auto relative overflow-hidden;

      .register {
        max-width: 245px;
        @apply w-full;

        p {
          @apply text-white text-center leading-none;
        }

        img {
          transform: scale(.9);
        }

        .divider {
          @apply w-full relative;

          hr {
            @apply text-white bg-white absolute pin-t;
            position: absolute;
            height: 2px;
            width: 165%;
            margin: 0;
            left: 30px;
          }
        }
      }
    }

    section {
      h6 {
        @apply text-white leading-none mb-0 py-7 text-center;
      }

      h2 {
        @apply text-f1-a;
      }

      h3 {
        @apply mb-0;
      }

      img {
        @apply w-full;
      }

      .features {
        @apply flex flex-wrap;

        &:nth-child(odd) {
          @apply bg-f1-c;
        }

        .feature {
          @apply py-6;

          p {
            @apply mb-0;
          }
        }
      }

      .additional-features {
        @apply pb-13;

        .additional-feature {
          ul {
            @apply px-3 m-0 text-f1-a;
          }
        }
      }
    }

    .news-carousel {
      width: 980px;
      margin: 8px auto 0;
      height: 300px;

      .news-reviews-slide {
        @apply flex flex-col justify-center p-3 bg-center bg-cover relative text-white;
        height: 300px;
        word-break: break-all;
      }
    }

    .cta-wrapper {
      @apply flex mt-8 mx-auto;

      @media (min-width: config('screens.xl')) {
        width: 920px;
      }

      .cta {
        @apply bg-lerner-blue py-5 px-7 flex items-center justify-between;
        border-radius: 45px;

        img {
          max-width: 216px;
          width: 100%;
        }

        a {
          position: relative;

          &:first-of-type:after {
            @apply bg-white absolute;
            content: '';
            width: 2px;
            height: 66px;
            right: -30px;
            top: 50%;
            transform: translateY(-50%);
          }
        }
      }
    }
}
</style>
